import React, { useCallback, useEffect, useState } from 'react';
import API from '../../../api/API';

/**
 * La coda delle scritture in attesa.
 *
 * Chi ha la scrittura con approvazione non scrive: la mutazione finisce qui col suo payload e viene
 * rieseguita all'approvazione, a nome di chi l'aveva chiesta. Si vedono le richieste che si possono
 * decidere piu' le proprie, che si vedono ma non si decidono.
 */
const etichette = {
  PENDING: 'in attesa',
  APPROVED: 'approvata',
  REJECTED: 'rifiutata'
};

const TabApprovazioni = ({ onCambiato }) => {
  const [stato, setStato] = useState('PENDING');
  const [richieste, setRichieste] = useState([]);
  const [caricando, setCaricando] = useState(true);
  const [errori, setErrori] = useState([]);
  const [inCorso, setInCorso] = useState(null);

  const carica = useCallback(async () => {
    setCaricando(true);
    setErrori([]);
    const res = await API.approvazioni.elenco(stato || undefined);
    setRichieste(res.isSuccess ? (res.data || []) : []);
    if (!res.isSuccess) setErrori([res.errorMsg || 'Lettura della coda non riuscita']);
    setCaricando(false);
  }, [stato]);

  useEffect(() => { carica(); }, [carica]);

  const decidi = async (richiesta, approva) => {
    let note = null;
    if (!approva) {
      note = window.prompt(`Perche' rifiuti la richiesta di ${richiesta.richiedente}?`, '');
      if (note === null) return;
    }

    setInCorso(richiesta.id);
    setErrori([]);

    const res = approva
      ? await API.approvazioni.approva(richiesta.id)
      : await API.approvazioni.rifiuta(richiesta.id, note);

    setInCorso(null);

    if (!res.isSuccess) {
      const dati = res.data;
      setErrori(Array.isArray(dati) ? dati : [res.errorMsg || 'Operazione non riuscita']);
      return;
    }

    await carica();
    await onCambiato();
  };

  /** Il payload e' la mutazione serializzata: mostrarlo e' l'unico modo di sapere cosa si approva. */
  const leggibile = (payload) => {
    try {
      return JSON.stringify(JSON.parse(payload), null, 1);
    } catch {
      return payload;
    }
  };

  return (
    <>
      <div className="utenti-barra">
        <p className="utenti-nota">
          Approvare riesegue la scrittura a nome di chi l'ha chiesta. Se nel frattempo il permesso o
          i dati sono cambiati, la richiesta viene rifiutata con il motivo invece di essere eseguita.
        </p>
        <label className="permessi-selettori">
          <select value={stato} onChange={e => setStato(e.target.value)}>
            <option value="PENDING">in attesa</option>
            <option value="APPROVED">approvate</option>
            <option value="REJECTED">rifiutate</option>
            <option value="">tutte</option>
          </select>
        </label>
      </div>

      {errori.length > 0 && (
        <ul className="utenti-errore utenti-errore-lista">
          {errori.map((e, i) => <li key={i}>{e}</li>)}
        </ul>
      )}

      {caricando && (
        <div className="utenti-loading"><div className="loading-spinner"></div><p>Caricamento...</p></div>
      )}

      {!caricando && richieste.length === 0 && (
        <div className="utenti-vuoto">
          <i className="fas fa-inbox"></i>
          <p>Nessuna richiesta.</p>
        </div>
      )}

      {!caricando && richieste.length > 0 && (
        <div className="utenti-tabella-wrap">
          <table className="utenti-tabella">
            <thead>
              <tr>
                <th>Chiesta da</th>
                <th>Cosa</th>
                <th>Ambito</th>
                <th>Quando</th>
                <th>Stato</th>
                <th>Dettaglio</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {richieste.map(r => (
                <tr key={r.id}>
                  <td>
                    <strong>{r.richiedente}</strong>
                    {r.mia && <span className="utenti-io"> (tu)</span>}
                  </td>
                  <td><code>{r.azione}</code> <code>{r.risorsa}</code></td>
                  <td>{r.scopeTipo === 'GLB' ? 'globale' : `${r.scopeTipo}:${r.scopeId}`}</td>
                  <td>{new Date(r.creatoIl).toLocaleString('it-IT')}</td>
                  <td>
                    <span className="utenti-tag">{etichette[r.stato] || r.stato}</span>
                    {r.approvatore && <span className="utenti-nota"> da {r.approvatore}</span>}
                    {r.note && <div className="utenti-nota">{r.note}</div>}
                    {r.idCreato && <div className="utenti-nota">ha creato la riga {r.idCreato}</div>}
                  </td>
                  <td>
                    <details>
                      <summary className="utenti-nota">payload</summary>
                      <pre className="approvazioni-payload">{leggibile(r.payload)}</pre>
                    </details>
                  </td>
                  <td className="utenti-azioni">
                    <button className="btn btn-success btn-sm" title={r.mia ? 'La tua richiesta la decide un altro' : 'Approva'}
                            disabled={r.stato !== 'PENDING' || r.mia || inCorso === r.id}
                            onClick={() => decidi(r, true)}>
                      <i className="fas fa-check"></i>
                    </button>
                    <button className="btn btn-delete btn-sm" title={r.mia ? 'La tua richiesta la decide un altro' : 'Rifiuta'}
                            disabled={r.stato !== 'PENDING' || r.mia || inCorso === r.id}
                            onClick={() => decidi(r, false)}>
                      <i className="fas fa-times"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default TabApprovazioni;
